import type { ReactNode } from 'react';

/**
 * Các mảnh giao diện dùng chung.
 *
 * Lớp Tailwind lấy nguyên văn từ DOM của bản đang chạy (đọc qua CDP
 * ngày 14/09/2026), không viết lại theo cảm nhận. Gom vào đây để tám màn
 * dùng chung một kiểu, thay vì mỗi màn tự chế một dáng thẻ khác nhau.
 */

const VIEN_THE =
  'rounded-xl border border-slate-200/80 bg-white shadow-card dark:border-slate-700/60 dark:bg-slate-800';

/** Khung thẻ chuẩn. Mọi khối nội dung trong dashboard đều nằm trong cái này. */
export function The({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={VIEN_THE + ' ' + className}>{children}</div>;
}

/** Đầu thẻ: biểu tượng + tiêu đề + số đếm + chỗ để nút bên phải. */
export function DauThe({
  icon, tieuDe, dem, phai,
}: { icon: ReactNode; tieuDe: string; dem?: number | string; phai?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5 dark:border-slate-700/60">
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-slate-400">{icon}</span>
        <h3 className="truncate text-sm font-bold text-slate-900 dark:text-white">{tieuDe}</h3>
        {dem !== undefined && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {dem}
          </span>
        )}
      </div>
      {phai && <div className="shrink-0">{phai}</div>}
    </div>
  );
}

/** Thẻ số liệu: biểu tượng, con số lớn, nhãn nhỏ bên dưới. */
export function TheSoLieu({
  icon, so, nhan,
}: { icon: ReactNode; so: ReactNode; nhan: string }) {
  return (
    <div className={VIEN_THE + ' p-4'}>
      <span className="text-slate-400">{icon}</span>
      <p className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">{so}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{nhan}</p>
    </div>
  );
}

export function NutPhu({
  children, onClick, disabled,
}: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50"
    >
      {children}
    </button>
  );
}

export function NutChinh({
  children, onClick, disabled,
}: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Nhãn trạng thái tròn, có chấm màu bên trái. */
export function NhanTrangThai({
  chu, mau,
}: { chu: string; mau: 'xanh' | 'xam' | 'vang' | 'do' }) {
  const nen = {
    xanh: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
    xam: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
    vang: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
    do: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
  }[mau];
  const cham = { xanh: 'bg-emerald-500', xam: 'bg-slate-400', vang: 'bg-amber-500', do: 'bg-rose-500' }[mau];
  return (
    <span className={'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ' + nen}>
      <span className={'h-1.5 w-1.5 rounded-full ' + cham} />
      {chu}
    </span>
  );
}

/**
 * Ảnh đại diện có đường lui.
 *
 * Vì sao cần: ảnh Facebook có hạn dùng (tham số oe= trong URL) và sẽ hỏng sau
 * vài ngày. Bản cũ để thẻ <img> gãy, hiện ô trống méo mó. Ở đây hỏng thì hiện
 * chữ cái đầu của tên — vẫn nhận ra được là tài khoản nào.
 */
export function AnhDaiDien({
  src, ten, kichThuoc = 'h-9 w-9',
}: { src?: string | null; ten: string; kichThuoc?: string }) {
  const chu = (ten || '?').trim().slice(0, 1).toUpperCase();
  return (
    <div className={'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700 ' + kichThuoc}>
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <span className="text-xs font-bold text-slate-500 dark:text-slate-300">{chu}</span>
      )}
    </div>
  );
}

/** Khối thông báo khi chưa có dữ liệu. Nói rõ phải làm gì, không chỉ báo trống. */
export function ChuaCoDuLieu({ chu, goiY }: { chu: string; goiY?: string }) {
  return (
    <div className="px-5 py-10 text-center">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{chu}</p>
      {goiY && <p className="mt-1 text-xs text-slate-400">{goiY}</p>}
    </div>
  );
}

/** Khung xương lúc đang tải. Giữ đúng chiều cao để trang không nhảy. */
export function DangTai({ dong = 3 }: { dong?: number }) {
  return (
    <div className="animate-pulse space-y-3 p-5">
      {Array.from({ length: dong }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-700" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-2.5 w-1/4 rounded bg-slate-100 dark:bg-slate-700/60" />
          </div>
        </div>
      ))}
    </div>
  );
}
